//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import $ from 'jquery';

import { CollectionType } from '@apis/collections/CollectionType';
import { ICollectionFiltersDto } from '@apis/collections/ICollectionFiltersDto';
import { ICollectionResponse } from '@apis/collections/ICollectionResponse';
import { ICollectionSourcesDto } from '@apis/collections/ICollectionSourcesDto';
import {
  CollectionUpdateNameRequest,
  CollectionUpdateRequest,
  DeleteCollectionRequest,
  GetCollectionsByTypesResponse,
} from '@apis/collections/collectionsApiModel';
import { ICollectionCreateRequest } from '@apis/collections/iCollectionCreateRequest';
import { ICollectionAdd } from '@collections/redux/collectionsModel';
import {
  serializedGatewayFetch,
  serializedSessionFetch,
  sessionFetch,
} from '@utils/sessionFetch';

const COLLECTIONS_BASE_URL = '/api/collections';

/**
 * Creates a new Stream
 * /api/collections
 *
 * @param name - The name of the Stream.
 * @param streamSources - The sources for the new Stream.
 * @param streamFilters - The filters for the new Stream
 * @param collectionType - The type of the new collection
 *
 * @returns Promise<ICollectionResponse>
 */
export async function createStream(
  name: string,
  streamSources: ICollectionSourcesDto,
  streamFilters: ICollectionFiltersDto,
  collectionType: CollectionType = CollectionType.STREAM,
): Promise<ICollectionResponse> {
  return createCollection({
    collectionType,
    name,
    sources: streamSources,
    filters: streamFilters,
  });
}

export async function createCollection(
  params: ICollectionCreateRequest,
): Promise<ICollectionResponse> {
  const res = await sessionFetch(COLLECTIONS_BASE_URL, {
    method: 'POST',
    body: JSON.stringify(params),
  });

  return await res.json();
}

/**
 * Get list of Collections.
 * /api/collections
 *
 * @param paramsObj - Optional query parameters.
 * @param init - Optional initialization options.
 *
 * @returns Promise<ICollectionResponse[]>
 */
export async function getCollections(
  paramsObj: object | undefined,
  init?: RequestInit,
): Promise<ICollectionResponse[]> {
  const paramsString = paramsObj ? `?${$.param(paramsObj)}` : '';
  const res = await sessionFetch(
    `${COLLECTIONS_BASE_URL}${paramsString}`,
    init,
  );

  return await res.json();
}

/**
 * Updates the Stream with the given ID.
 * /api/collections/:streamId
 *
 * @param streamId - The ID of the Stream to update.
 * @param name - The name of the Stream to set.
 * @param streamSources - The updated Stream sources.
 * @param streamFilters - The updated Stream filters.
 *
 * @returns Promise<ICollectionResponse>
 */
export async function updateStream(
  streamId: string,
  name: string,
  streamSources: ICollectionSourcesDto,
  streamFilters: ICollectionFiltersDto,
): Promise<ICollectionResponse> {
  const res = await sessionFetch(`${COLLECTIONS_BASE_URL}/${streamId}`, {
    method: 'PUT',
    body: JSON.stringify({
      name,
      sources: streamSources,
      filters: streamFilters,
    }),
  });

  return await res.json();
}

export async function addSourceToCollection(
  addRequest: ICollectionAdd,
): Promise<ICollectionResponse> {
  const { collectionEntityType, collectionId, secondarySourceId, sourceId } =
    addRequest;

  const params = {
    sourceId,
    sourceType: collectionEntityType,
    secondarySourceId,
    network: 'Instagram',
  };

  const res = await sessionFetch(
    `${COLLECTIONS_BASE_URL}/${collectionId}/sources`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
  );
  return await res.json();
}
/**
 * Gets the list of Streams from Collection Service.
 * /api/collections
 *
 * @param params - Pagination and filtering options
 *
 * @returns Promise<ICollectionResponse[]>
 */
export async function getStreams(
  collectionType: CollectionType = CollectionType.STREAM,
): Promise<ICollectionResponse[]> {
  const paramsObj = {
    collectionType,
  };
  return getCollections(paramsObj);
}

export async function getCollectionsByTypes(
  collectionTypes: CollectionType[],
  { isModeration, ...init }: RequestInit & { isModeration?: boolean } = {},
): Promise<ICollectionResponse[]> {
  const params = new URLSearchParams();
  collectionTypes.forEach((type) => params.append('collectionTypes', type));

  if (isModeration) {
    params.append('isModeration', 'true');
  }

  const res = await serializedGatewayFetch(
    'web_gateway',
    `/api/v1/collections?${params.toString()}`,
    init,
  );

  const json = await res.json<GetCollectionsByTypesResponse>();
  return json.data.collectionDto;
}
/**
 * Deletes a stream
 *
 * @param collectionId
 *
 * @returns An empty promise
 */
export const deleteStream = async (collectionId: string): Promise<Response> => {
  return await sessionFetch(`${COLLECTIONS_BASE_URL}/${collectionId}`, {
    method: 'DELETE',
  });
};

/**
 * Updates a given collections name
 *
 * /api/collections/:collectionId/name
 *
 * @param collectionId the id of the collection being updated
 * @param name the new name to update the collection with
 */
export async function updateSavedCollectionName(
  { collectionId, name }: CollectionUpdateNameRequest,
  init?: RequestInit,
): Promise<ICollectionResponse> {
  const res = await sessionFetch(
    `${COLLECTIONS_BASE_URL}/${collectionId}/name`,
    {
      ...init,
      method: 'PUT',
      body: JSON.stringify({
        name,
      }),
    },
  );

  return res.json();
}

/**
 * DELETE /api/collections/:collectionId
 * @param collectionId the id of the collection being deleted
 */
export async function deleteCollection(
  { collectionId }: DeleteCollectionRequest,
  init?: RequestInit,
): Promise<void> {
  await sessionFetch(`${COLLECTIONS_BASE_URL}/${collectionId}`, {
    ...init,
    method: 'DELETE',
  });
}

/**
 * Updates a given collection
 *
 * /api/collections/:collectionId
 *
 * @param collectionId the id of the collection being updated
 * @param sources - The updated sources.
 * @param filters - The updated filters.
 * @param sort - The updated sort.
 * @param name - The collection name.
 */
export async function updateSavedCollection(
  { collectionId, ...body }: CollectionUpdateRequest,
  init?: RequestInit,
): Promise<ICollectionResponse> {
  const res = await serializedSessionFetch(
    `${COLLECTIONS_BASE_URL}/${collectionId}`,
    {
      ...init,
      method: 'PUT',
      body: JSON.stringify(body),
    },
  );

  return res.json<ICollectionResponse>();
}
